import { ClerkProvider } from '@clerk/react';
import { useAuth } from '@clerk/react';
import { useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  registerClerkTokenProvider,
  clearClerkTokenProvider,
} from '@client/api/clerkTokenProvider';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function ClerkTokenBridge({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      registerClerkTokenProvider(options => getTokenRef.current(options));
    } else {
      clearClerkTokenProvider();
    }
    return () => clearClerkTokenProvider();
  }, [isLoaded, isSignedIn]);

  return <>{children}</>;
}

export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  // Hand Clerk our React Router navigate so its internal navigations (the
  // "Sign up" link, post-auth redirects) stay client-side instead of doing a
  // full-page reload via window.location. Requires this provider to live inside
  // <BrowserRouter> (see main.tsx). Called unconditionally to satisfy the rules
  // of hooks before the no-key early return below.
  const navigate = useNavigate();

  if (!CLERK_PUBLISHABLE_KEY) {
    return <>{children}</>;
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      signInFallbackRedirectUrl="/"
      routerPush={to => navigate(to)}
      routerReplace={to => navigate(to, { replace: true })}
    >
      <ClerkTokenBridge>{children}</ClerkTokenBridge>
    </ClerkProvider>
  );
}
